import { Component, OnInit, SimpleChange, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription, take, takeUntil } from 'rxjs';
import { LinkedinConnectionsService } from 'src/app/services/linkedin-connections/linkedin-connections.service';
import { AlertComponent } from 'src/app/shared/alert/alert/alert.component';
import { PlaceHolderDirective } from 'src/app/shared/directives/placeholder.directive';
import { useLinkedinConnectionsStore } from 'src/app/store/linkedin-connections';
import { useModalManagerStore } from 'src/app/store/modal-manager.store';
import { useToastStore } from 'src/app/store/toast.store';
import {
  AnalyticsManagerService,
  Events,
} from 'src/app/shared/services/analytics-manager.service';
import { LinkedinLeadsService } from 'src/app/leads/services/linkedin-leads.service';
import * as LeadListActions from '../../store/lead-list/lead-list.actions';
import * as fromRoot from 'src/app/store/app.reducer';
import { userProfileFeatureKey } from 'src/app/store/user/user.reducer';

@Component({
    selector: 'activity-log-modal',
    templateUrl: './activity-log-modal.component.html',
    styleUrls: ['./activity-log-modal.component.scss'],
    standalone: false
})
export class ActivityLogModalComponent implements OnInit {
  @ViewChild(PlaceHolderDirective, { static: false })
  promptHost: PlaceHolderDirective;
  isVisible: boolean;
  connection: any;
  connectionProfile: any;
  activityData: any;
  messages: any[] = [];
  userProfileImage: string; // Default profile image
  isLoading: boolean;
  isLoadingMessages: boolean = false;
  isSendMessageActive: boolean = false;
  isLoadingMessageSend: boolean = false;
  message: string = '';
  activeTab: 'messages' | 'activity' = 'activity'; // Default tab
  markAsCompletePrompt =
    'When marking your connection as complete pipa will no longer track your activity. You can always come back and set them to active again.';
  markAsActivePrompt =
    'When marking your connection as active pipa will start tracking your activity again.';

  private dismissPromptSub$: Subscription;
  private promptActionSub$: Subscription;

  constructor(
    private linkedinConnectionsService: LinkedinConnectionsService,
    private linkedinLeadsService: LinkedinLeadsService,
    private store: Store<fromRoot.AppState>
  ) {
    this.isVisible = useModalManagerStore.getState().activityLog;
    this.isLoading = useLinkedinConnectionsStore.getState().isActivityLoading;
  }

  onClose() {
    this.connection = null;
    useLinkedinConnectionsStore.getState().reset();
    useModalManagerStore.setState({ activityLog: false });
    this.message = '';
    this.messages = [];
    this.isSendMessageActive = false;
    this.isLoadingMessageSend = false;
    this.activeTab = 'activity';
  }

  ngOnInit(): void {
    if (!this.userProfileImage) this.getUser();
    useModalManagerStore.subscribe((state) => {
      this.isVisible = state.activityLog;
      if (this.isVisible) {
        this.getActivities();
        if (this.connection?.connection_name) {
          this.fetchMessages();
        }
      }
    });
  }

  setActiveTab(tab: 'messages' | 'activity'): void {
    this.activeTab = tab;
    if (
      tab === 'messages' &&
      !this.messages.length &&
      this.connection?.connection_name
    ) {
      this.fetchMessages();
    }
  }

  fetchMessages(): void {
    if (this.isLoadingMessages) return;
    this.isLoadingMessages = true;
    this.linkedinConnectionsService
      .getLinkedinConversation(this.connection.id)
      .subscribe({
        next: (res: any) => {
          this.messages = res || [];
          this.isLoadingMessages = false;
        },
        error: () => {
          this.isLoadingMessages = false;
          this.messages = [];
        },
      });
  }

  getActivities() {
    useLinkedinConnectionsStore.subscribe((state) => {
      const connectionData = state.activeConnection;
      this.activityData = state?.connectionActivity;
      this.isLoading = state.isActivityLoading;
      connectionData?.find((c) => (this.connection = c));
    });
  }

  deleteLead(lead): void {
    if (!lead || !lead.id) return;
    this.linkedinLeadsService.deleteLeads([lead.id]).subscribe({
      next: () => {
        this.store.dispatch(LeadListActions.fetchLeadListsStart());
        useToastStore.setState({
          isVisible: true,
          content: `Successfully deleted lead.`,
          variant: 'success',
        });
      },
      error: () => {
        useToastStore.setState({
          isVisible: true,
          content: 'Error deleting leads. Please try again.',
          variant: 'danger',
        });
      },
    });
  }

  showPrompt(
    variant: 'active' | 'complete' | 'delete',
    actionButtonLabel = 'Continue',
    theme = 'primary'
  ) {
    if (variant === this.markAsCompletePrompt) {
      AnalyticsManagerService.recordButtonClick(
        Events.toggleMarkConnectionAsComplete
      );
    } else if (variant === this.markAsActivePrompt) {
      AnalyticsManagerService.recordButtonClick(
        Events.toggleMarkConnectionAsActive
      );
    }
    useModalManagerStore.setState({ activityLog: false });
    const viewContainerRef = this.promptHost.viewContainerRef;
    viewContainerRef.clear();
    const ref = viewContainerRef.createComponent(AlertComponent);
    ref.instance.hasAction = true;
    ref.instance.message = variant;
    ref.instance.theme = theme;
    ref.instance.label = actionButtonLabel;
    if (variant === 'complete') {
      ref.instance.message = this.markAsCompletePrompt;
    } else if (variant === 'active') {
      ref.instance.message = this.markAsActivePrompt;
    } else if (variant === 'delete') {
      ref.instance.message = 'Are you sure you want to delete this lead?';
      ref.instance.cancelLabel = 'Nevermind';
      ref.instance.label = 'Delete';
      ref.instance.theme = 'danger';
    }
    this.promptActionSub$ = ref.instance.onClick.subscribe(() => {
      this.promptActionSub$.unsubscribe();
      viewContainerRef.clear();
      if (variant === 'complete') {
        AnalyticsManagerService.recordButtonClick(
          Events.markConnectionAsCompleteClicked
        );
        this.markConnectionAsComplete();
        useModalManagerStore.setState({ activityLog: true });
      } else if (variant === 'active') {
        AnalyticsManagerService.recordButtonClick(
          Events.markConnectionAsCompleteClicked
        );
        this.markConnectionAsActive();
        useModalManagerStore.setState({ activityLog: true });
      } else if (variant === 'delete') {
        this.deleteLead(this.connection);
        useModalManagerStore.setState({ activityLog: false });
      }
    });
    this.dismissPromptSub$ = ref.instance.close.subscribe(() => {
      this.dismissPromptSub$.unsubscribe();
      viewContainerRef.clear();
      useModalManagerStore.setState({ activityLog: true });
    });
  }

  getUser() {
    this.store.select(userProfileFeatureKey).subscribe((userState) => {
      this.userProfileImage = userState?.ClientLinkedinMetaDatum?.profile_image;
    });
  }

  markConnectionAsComplete() {
    const payload = { details: 'You marked this connection as complete' };
    this.linkedinConnectionsService
      .markConnectionAsComplete(this.connection.id, payload)
      .subscribe((res) => {
        if (!res.hasOwnProperty('errors')) {
          useToastStore.setState({
            isVisible: true,
            content: 'Connection marked as complete',
          });
        }
      });
  }

  markConnectionAsActive() {
    const payload = { details: 'You marked this connection as active' };
    this.linkedinConnectionsService
      .markConnectionAsActive(this.connection.id, payload)
      .subscribe((res) => {
        if (!res.hasOwnProperty('errors')) {
          useToastStore.setState({
            isVisible: true,
            content: 'Connection marked as active',
          });
        }
      });
  }

  onSendMessageChange(e) {
    this.message = e.target.value.trim();
  }

  onToggleMessageInput() {
    AnalyticsManagerService.recordButtonClick(Events.toggleSendMessage);
    this.isSendMessageActive = !this.isSendMessageActive;
  }

  onSendMessageClick() {
    this.isLoadingMessageSend = true;
    if (this.message) {
      AnalyticsManagerService.recordButtonClick(Events.sendMessageClicked);
      this.linkedinConnectionsService
        .sendMessage(
          this.message,
          this.connection.id,
          this.connection.connection_name
        )
        .subscribe((res) => {
          this.isLoadingMessageSend = false;

          useToastStore.setState({
            isVisible: true,
            content: 'Message sent successfully',
          });
        }),
        (err) => {
          this.isLoadingMessageSend = false;
          useToastStore.setState({
            isVisible: true,
            content: 'Opps failed to send message, lets try that again',
            variant: 'danger',
          });
        };
    }
  }
}
